import {
  InboxOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Drawer,
  Flex,
  Form,
  Select,
  Typography,
  Upload,
} from 'antd';
import LocationSelectInput from 'components/LocationSelectInput';
import useIsMobile from 'hooks/useIsMobile';
import useLoadings from 'hooks/useLoadings';
import React, {
  useState,
} from 'react';
import http, { getHttpErrorMessage } from 'utils/http';
import { COMPANY_STATUS_OPTIONS } from '../utils';

const { Text, Link } = Typography;
const { Dragger } = Upload;

const REQUIRED_COLUMNS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'role',
];

function ImportCsvDrawer({ open, onClose }) {
  const { message } = App.useApp();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [isValidated, setIsValidated] = useState(false);
  const { setLoading, isLoading } = useLoadings();

  // Reset state when drawer opens/closes
  React.useEffect(() => {
    if (open) {
      form.resetFields();
      setFileList([]);
      setValidationError(null);
      setIsValidated(false);
    }
  }, [open, form]);

  const parseAndValidateCsvText = (text, file, isLargeFile) => {
    const lines = text.split('\n').filter((line) => line.trim());

    if (lines.length < 2) {
      throw new Error('CSV file appears to be empty or corrupted.');
    }

    // Parse header row
    const headerRow = lines[0].trim();
    const headers = headerRow.split(',').map((header) => header.trim().replace(/"/g, ''));

    // Check for required columns
    const missingRequired = REQUIRED_COLUMNS.filter((column) => !headers.some((header) => header.toLowerCase() === column.toLowerCase()));

    if (missingRequired.length > 0) {
      throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
    }

    // All headers are expected since we only have required columns
    const unexpectedFields = headers.filter((header) => !REQUIRED_COLUMNS.some((column) => column.toLowerCase() === header.toLowerCase()));

    return {
      isValid: true,
      headers,
      missingRequired: [],
      unexpectedFields,
      fileSize: file.size,
      isLargeFile,
    };
  };

  const validateCsvFile = (file) => new Promise((resolve, reject) => {
    const CHUNK_SIZE = 8192 * 2; // 16KB
    const isLargeFile = file.size > 1024 * 1024; // 1MB threshold

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const validation = parseAndValidateCsvText(text, file, isLargeFile);
        resolve(validation);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read the file.'));
    };

    // Read appropriate amount based on file size
    if (isLargeFile) {
      const blob = file.slice(0, CHUNK_SIZE);
      reader.readAsText(blob);
    } else {
      reader.readAsText(file);
    }
  });

  const handleTemplateDownload = () => {
    // Create a sample CSV template using required columns
    const csvContent = `${REQUIRED_COLUMNS.join(',')}\n` +
      'John,Doe,+1234567890,john.doe@example.com,owner\n' +
      'Jane,Smith,+1987654321,jane.smith@example.com,manager';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    fileList,
    beforeUpload: (file) => {
      // Only allow CSV files
      const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      if (!isCsv) {
        message.error('You can only upload CSV files!');
        return false;
      }

      // Check file size (max 100MB for CSV imports)
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        message.error('File must be smaller than 100MB! For larger files, please split them into smaller chunks.');
        return false;
      }

      // Warning for large files
      const fileSizeMB = file.size / 1024 / 1024;
      if (fileSizeMB > 10) {
        message.warning(`Large file detected (${fileSizeMB.toFixed(1)}MB). Validation will check headers only. Full processing will happen during import.`);
      }

      return false; // Prevent auto upload
    },
    onChange: async (info) => {
      const newFileList = info.fileList.slice(-1); // Keep only the latest file
      setFileList(newFileList);
      setValidationError(null);
      setIsValidated(false);

      if (newFileList.length > 0) {
        const file = newFileList[0].originFileObj;
        if (file) {
          try {
            setLoading('validate_csv');
            const validation = await validateCsvFile(file);
            setIsValidated(true);

            if (validation.unexpectedFields.length > 0) {
              message.warning(
                `Warning: Unexpected fields found: ${validation.unexpectedFields.join(', ')}. These will be ignored during import.`,
              );
            }
          } catch (error) {
            setValidationError(error.message);
            setIsValidated(false);
          } finally {
            setLoading('validate_csv', false);
          }
        }
      }
    },
    onDrop: (e) => {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const handleImport = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error('Please fill all required fields');
      return;
    }

    if (fileList.length === 0) {
      message.error('Please select a CSV file to upload');
      return;
    }

    if (!isValidated) {
      message.error('Please wait for CSV validation to complete or upload a valid CSV file');
      return;
    }

    if (validationError) {
      message.error('Cannot import CSV with validation errors. Please fix the errors and try again.');
      return;
    }

    setLoading('import_csv');

    const formData = new FormData();
    formData.append('file', fileList[0].originFileObj);
    formData.append('company_status', values.company_status);
    formData.append('market', values.market);

    try {
      const response = await http.post('/v1/organizations/{{organizationId}}/company-contact-batches/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setLoading('import_csv', false);

      if (response.data?.message) {
        message.success(response.data.message);
      }

      onClose();
    } catch (error) {
      setLoading('import_csv', false);
      message.error({
        content: getHttpErrorMessage(error),
        duration: 5,
      });
    }
  };

  const renderInstructions = () => (
    <div className="mb-6">
      <Text strong className="mb-4 block text-lg">Upload contacts here</Text>
      <ol className="mb-4 space-y-2">
        <li className="flex">
          <span className="mr-3 font-medium">1.</span>
          <span>Upload your contact list into Omnicart using the template below</span>
        </li>
        <li className="flex">
          <span className="mr-3 font-medium">2.</span>
          <span>Our AI will automatically enrich your data and create detailed company profiles</span>
        </li>
        <li className="flex">
          <span className="mr-3 font-medium">3.</span>
          <span>Once the process is complete, you&apos;ll receive a confirmation email</span>
        </li>
        <li className="flex">
          <span className="mr-3 font-medium">4.</span>
          <span>You can view your newly added contacts and companies in the Companies tab by filtering by Creation Date and Status</span>
        </li>
      </ol>
      <Link
        onClick={handleTemplateDownload}
        className="text-green-600 hover:text-green-700"
      >
        template.csv
      </Link>
    </div>
  );

  const renderDefaultSettings = () => (
    <Flex gap={16} className="mb-6">
      <Form.Item
        name="company_status"
        label="Status"
        className="flex-1"
        // rules={[{ required: true, message: 'Please select a default status' }]}
      >
        <Select
          allowClear
          options={COMPANY_STATUS_OPTIONS}
          size="large"
          placeholder="Select a default status"
        />
      </Form.Item>
      <Form.Item
        name="market"
        label="Market"
        className="flex-1"
        // rules={[{ required: true, message: 'Please select a default market' }]}
      >
        <LocationSelectInput
          placeholder="Select a default market"
          mode={undefined}
          labelInValue={false}
          style={{ width: '100%' }}
          size="large"
        />
      </Form.Item>
    </Flex>
  );

  const renderFileUpload = () => (
    <Form.Item className="mb-6">
      <Dragger {...uploadProps} className="hover:border-green-400">
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: '#52c41a' }} />
        </p>
        <p className="ant-upload-text text-lg font-medium">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint text-gray-500">
          Support for a single or bulk upload. Strictly prohibit from uploading company data or other band files
        </p>
      </Dragger>

      {/* Validation Status */}
      {fileList.length > 0 && (
        <div className="mt-4">
          {isLoading('validate_csv') && (
            <Alert
              message={
                fileList[0]?.originFileObj?.size > 1024 * 1024
                  ? 'Validating large CSV file (headers only)...'
                  : 'Validating CSV file...'
              }
              description={
                fileList[0]?.originFileObj?.size > 1024 * 1024
                  ? 'Large files are validated efficiently by checking only the header structure.'
                  : undefined
              }
              type="info"
              showIcon
            />
          )}

          {validationError && (
            <Alert
              message="Please upload a valid file using the provided template."
              type="error"
              showIcon={false}
            />
          )}
        </div>
      )}
    </Form.Item>
  );

  const renderFooter = () => (
    <Flex justify="end" gap={8}>
      <Button
        onClick={onClose}
        disabled={isLoading('import_csv')}
        size="large"
      >
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleImport}
        loading={isLoading('import_csv')}
        disabled={fileList.length === 0 || !isValidated || !!validationError || isLoading('validate_csv')}
        size="large"
      >
        Import
      </Button>
    </Flex>
  );

  return (
    <Drawer
      title="Import"
      placement="right"
      width={isMobile ? '100%' : 720}
      onClose={onClose}
      open={open}
      styles={{ body: { padding: '16px 24px' } }}
      footer={renderFooter()}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {renderInstructions()}
        {renderDefaultSettings()}
        {renderFileUpload()}
      </Form>
    </Drawer>
  );
}

export default ImportCsvDrawer;
